import { Form, Formik } from "formik";
import React, { useEffect, useState } from "react";
import { FormText } from "react-bootstrap";
import { toast } from "react-toastify";
import * as yup from "yup";
import FormikCheckbox from "../../components/Forms/FormikCheckbox";
import LoaderButton from "../../components/LoaderButton";
import useAuth from "../../contexts/useAuth";
import { Constants } from "../../libs/Constants";
import { update } from "../../libs/dbFunctions";
import { placeEvent } from "../../libs/eventsLib";
import { formatCurrency, formatDate, getCurrentDate } from "../../libs/utils";
import "./LoanAgreement.scss";

const initialValues = {
  acceptTerms: false,
};
const schema = yup.object({
  acceptTerms: yup
    .boolean()
    .required("Please agree to loan terms and conditions")
    .oneOf([true], "Please agree to loan terms and conditions"),
});

const LoadAgreement = ({ periodsPerAnnum, user, onSuccess, onCancel }) => {
  const [lendingScrolled, setLendingScrolled] = useState(false);
  const [contribution, setContribution] = useState(0);
  const [checkboxClicked, setCheckboxClicked] = useState(false);
  const { activeCompany } = useAuth();

  useEffect(() => {
    function calculateContribution() {
      if (!user) {
        return;
      }
      let contributionPerAnnum = 0;
      if (user.contributionAmount && periodsPerAnnum) {
        contributionPerAnnum =
          (typeof contributionAmount === "string"
            ? Number(user.contributionAmount)
            : user.contributionAmount) * periodsPerAnnum;
      }
      setContribution(contributionPerAnnum);
    }
    calculateContribution();
  }, [user?.contributionAmount, periodsPerAnnum]);

  const handleSubmit = async (values) => {
    let saveFields = {
      id: user.id,
      acceptTerms: values.acceptTerms,
    };
    //update the userObj with salary and flags
    try {
      await update(Constants.dbTypes.USER, saveFields);
      await placeEvent(Constants.EVENT_TYPES.ACCEPT_TERMS, user.email, {
        acceptTerms: values.acceptTerms,
      });
      onSuccess(saveFields);
    } catch (e) {
      if (e.response && e.response.data && e.response.data.error) {
        toast.error(e.response.data.error);
      } else {
        toast.error(e);
      }
    }
  };

  const handleOnscroll = (event) => {
    const form = event.currentTarget;
    let formScrolPosition =
      form.scrollHeight - Math.ceil(form.scrollTop) - form.clientHeight;
    if (formScrolPosition <= 0) {
      setLendingScrolled(true);
    }
  };

  return (
    <>
      <h2 className="text-center pb-3">Don’t forget, we’re giving you money</h2>
      <div className="box-agreement font-secondary">
        <div className="font-12 fw-bold pt-3 ps-4">
          EMPLOYEE STOCK PURCHASE PROGRAM LOAN AGREEMENT
        </div>
        <div
          className="plan-scroll thin-scroll p-4"
          onScroll={(e) => handleOnscroll(e)}
        >
          <div className="font-12 font-weight-medium pt-1">
            THIS LOAN AGREEMENT, dated as of{" "}
            {formatDate(getCurrentDate(), Constants.DATEFORMAT.MMDDYYYY)} (this
            “Agreement”), between Taabl Co., a Delaware corporation (“Lender”),
            and {user.firstName} {user.lastName}, having an address at{" "}
            {user?.address}, {user?.city}, {user?.state}, {user?.country},{" "}
            {user?.zip} (“Borrower”).
          </div>
          <div className="font-12 text-center fw-bold pt-3 ps-4">RECITALS</div>
          <div className="font-12 font-weight-medium pt-1">
            WHEREAS, in connection with Borrower’s employment at{" "}
            {activeCompany.name} (“Employer”), Borrower intends or is enrolled
            in Employer’s Employee Stock Purchase Plan (the “ESPP”) pursuant to
            the ESPP agreement provided by the Employer;
          </div>
          <br></br>
          <div className="font-12 font-weight-medium pt-1">
            WHEREAS, Borrower intends to purchase shares of Employer stock (the
            “Shares”) pursuant to the ESPP;
          </div>
          <br></br>
          <div className="font-12 font-weight-medium pt-1">
            WHEREAS, Lender has agreed to periodically make contributions to the
            Borrower in a total amount annually of{" "}
            {formatCurrency(contribution)} (the “Loan”) to enable the Borrower
            to purchase shares of stock under the ESPP;“
          </div>
          <br></br>
          <div className="font-12 font-weight-medium pt-1">
            NOW, THEREFORE, in consideration of the mutual covenants and
            agreements hereinafter set forth and for other good and valuable
            consideration, the receipt and sufficiency of which are hereby
            acknowledged, the parties hereto agree as follows:
          </div>
          <div className="font-12 text-center fw-bold pt-3 ps-4">
            ARTICLE I AMOUNT AND TERMS OF THE LOAN
          </div>
          <ol type="1">
            <li className="font-12 font-weight-medium pt-3">
              <b>The Loan.</b> The “Loan” hereunder will mean the loan by Lender
              to Borrower, in the principal amount of up to{" "}
              {formatCurrency(contribution)} on an annual basis, to permit
              Borrower to purchase Shares under the ESPP. The Lender will
              advance the proceeds of the Loan to the Borrower to purchase
              Shares in accordance with the terms of the ESPP. The Lender shall
              advance amounts under the Loan to the Borrower via bi-monthly
              installments, or such other cycle as Employer may follow in
              collecting contributions from the Borrower for participating in
              the ESPP, or in a lump sum payment as Lender may agree. The
              initial installment or lump sum payment will occur on or before
              the end of the purchase period. The Lender may make multiple Loans
              under this Agreement. The terms of each such Loan, including the
              amount and fee, shall be set out in an addendum to this Agreement.
            </li>
            <li className="font-12 font-weight-medium pt-3">
              <b>Due Diligence.</b> Borrower agrees to complete such
              documentation as Lender may require to extend the Loan, and
              Borrower consents to such other due diligence as Lender may
              require, including, but not limited to, credit checks and lien
              searches.
            </li>
            <li className="font-12 font-weight-medium pt-3">
              <b>Rate of Return/Service Fee. </b> Borrower agrees to complete
              such documentation as Lender may require to extend the Loan, and
              Borrower consents to such other due diligence as Lender may
              require, including, but not limited to, credit checks and lien
              searches.
            </li>
            <li className="font-12 font-weight-medium pt-3">
              <b>Repayment. </b> Upon the distribution of the Borrower’s Shares
              purchased through the ESPP, which shall be evidenced in writing by
              a notice from the ESPP to Borrower, Borrower agrees to sell, a
              number of shares in an amount equal to the total amount of the
              Loan together with the fee, and any other interest, fees or
              charges under this Agreement (the “Proceeds”). If the sale of
              Shares results in an amount of cash in excess of the Proceeds,
              such amount shall be referred to as “Excess Proceeds.” Borrower
              further agrees that upon such sale, Borrower shall transfer the
              Proceeds to Lender using such means as agreed upon. Borrower
              further agrees to execute any agreements, documents or
              instructions necessary to complete the sales of the Shares. For
              avoidance of doubt, Borrower shall retain any remaining Shares not
              required to satisfy his or her payment obligations, together with
              any Excess Proceeds.
            </li>
            <li className="font-12 font-weight-medium pt-3">
              <b>Acceleration. </b> Immediately upon the occurrence of any Event
              of Default (as defined in Article VI) and during any continuance
              thereof, the Lender may declare the Loan, the fee, and all other
              amounts and obligations payable to be forthwith due and payable to
              the Lender or may take any other action as provided in Article VI
              herein.
            </li>
            <li className="font-12 font-weight-medium pt-3">
              <b>No Liens. </b> Lender hereby covenants and agrees that it shall
              not place any lien on the Shares or otherwise have recourse with
              respect to the Shares in the event that the loan is repaid by the
              borrower within reasonable time for borrower to sell purchased
              shares from their ESPP participation. If the borrower fails to
              participate in their ESPP or sell purchased shares, Lender may
              offer recourse for loaned funds.
            </li>
          </ol>
          <div className="font-12 text-center fw-bold pt-3 ps-4">
            ARTICLE II BORROWER’S REPRESENTATIONS AND WARRANTIES
          </div>
          <ol type="1">
            <li className="font-12 font-weight-medium pt-3">
              <b>Authority. </b> The Borrower has the requisite legal capacity
              to own his assets, to borrow money, to execute, deliver and
              perform his/her duties under this Agreement and the ESPP
              Agreement.
            </li>
            <li className="font-12 font-weight-medium pt-3">
              <b>Litigation and Liens.</b> There are no actions, suits,
              proceedings or governmental investigations or inquiries pending,
              or to the best knowledge of the Borrower threatened, against the
              Borrower, that could, if adversely determined, have a material
              adverse effect on the performance of any obligation contemplated
              in or arising under the Agreement (a “Material Adverse Effect”).
              Borrower is not subject to any liens that could be levied upon the
              Loan.
            </li>
            <li className="font-12 font-weight-medium pt-3">
              <b>Taxes. </b> ll federal, state, and local tax returns, reports
              and statements (collectively, the “Tax Returns”) required to be
              filed by the Borrower have been filed with the appropriate
              governmental agencies in all jurisdictions in which such Tax
              Returns are required to be filed (after giving effect to any
              extensions obtained for the filing thereof), all such Tax Returns
              are true and correct in all material respects, and all taxes,
              charges and other impositions due and payable have been timely
              paid prior to the date on which any fine, penalty, interest, late
              charge or loss may be added thereto for non-payment thereof.
              Proper and accurate amounts have been withheld by the Borrower for
              all periods in full and complete compliance with the tax, social
              security and unemployment withholding provisions for applicable
              federal, state and local law and such withholdings have been paid
              to the respective governmental authorities.
            </li>
            <li className="font-12 font-weight-medium pt-3">
              <b>Full Disclosure. </b> No written statement prepared or
              furnished to the Lender in connection with the transaction
              contemplated hereby by or on behalf of the Borrower, when all such
              statements taken as a whole, contains any untrue statement of a
              material fact or omits to state a material face necessary to make
              the statements contained therein not misleading. All facts known
              to the Borrower which are material to an understanding of the
              financial condition, business, properties or prospects of the
              Borrower have been disclosed to the Lender.
            </li>
            <li className="font-12 font-weight-medium pt-3">
              <b>No Burdensome Restrictions. </b>
              <ol type="a">
                <li className="font-12 font-weight-medium pt-3">
                  The Borrower is not a party to any contractual obligation the
                  compliance with which would have a Material Adverse Effect or
                  the performance of which, either unconditionally or upon the
                  happening of an event, will result in the creation of a lien
                  on the property or assets of the Borrower.
                </li>
                <li className="font-12 font-weight-medium pt-3">
                  The Borrower is not in default under or with respect to any
                  contractual obligation owed by the Borrower and, to the
                  knowledge of the Borrower, no other party is in default under
                  or with respect to any contractual obligation owed to the
                  Borrower, other than those defaults which in the aggregate
                  have no Material Adverse Effect.
                </li>
                <li className="font-12 font-weight-medium pt-3">
                  No Event of Default or event which, with the lapse of time
                  and/or notice, would become and Event of Default (such event,
                  a “Default”) has occurred and is continuing.
                </li>
                <li className="font-12 font-weight-medium pt-3">
                  There is no Applicable Law, as defined in Section 3.1(d), the
                  compliance with which by the Borrower would have a Material
                  Adverse Effect.
                </li>
              </ol>
            </li>
          </ol>
          <div className="font-12 text-center fw-bold pt-3 ps-4">
            ARTICLE III COVENANTS
          </div>
          <div className="font-12 font-weight-light pt-1">
            Borrower covenants and agrees that, as long as this Agreement is in
            effect:
          </div>
          <ol type="a">
            <li className="font-12 font-weight-medium pt-3">
              <u>Use of Proceeds.</u> The proceeds of the Loan shall be used by
              Borrower solely to finance Borrower’s purchase of shares pursuant
              to the ESPP.
            </li>
            <li className="font-12 font-weight-medium pt-3">
              <u>Filings.</u> Borrower shall timely make all requisite
              regulatory, federal, state and local governmental filings.
            </li>
            <li className="font-12 font-weight-medium pt-3">
              <u>Compliance with Laws.</u> Borrower shall comply with all
              applicable laws, rules, regulations and customs, including common
              law, and any amendments and supplements thereto, including,
              without limitation, those of all U.S. and non-U.S. federal, state
              and local governmental authorities, self-regulatory organizations,
              markets, exchanges and clearing facilities, in all cases where
              applicable (“Applicable Law”).
            </li>
            <li className="font-12 font-weight-medium pt-3">
              <u>Liens.</u> Borrower shall not incur, create, assume, or permit
              to exist any lien upon the Loan or shares issued from the loan.
            </li>
          </ol>
          <div className="font-12 text-center fw-bold pt-3 ps-4">
            ARTICLE IV TAXES
          </div>
          <div className="font-12 font-weight-light pt-1">
            Borrower understands that if Employer sells an amount of Shares as
            are necessary to meet Borrower’s tax obligations under the ESPP,
            such sale is in addition to the sale of Shares necessary to generate
            the Proceeds, as described in paragraph 1.4 of this Agreement. Sales
            of Shares for tax purposes are governed solely by any agreement
            between Borrower and Employer.
          </div>
          <div className="font-12 text-center fw-bold pt-3 ps-4">
            ARTICLE V TERMINATION
          </div>
          <ol type="1">
            <li className="font-12 font-weight-medium pt-3">
              <b>General. </b>Should Borrower terminate his/her participation in
              the ESPP at any time, or should Borrower’s employment with
              Employer change at any time, Borrower shall notify Employer and
              Lender immediately in writing. Upon the effective date of
              Borrower’s termination of his or her participation in the ESPP, or
              upon the effective date of the termination of Borrower’s
              employment with Employer, Lender shall be entitled to receive from
              Employer amounts that Lender has paid under the Loan in accordance
              with the terms of the agreement between Employer and Lender for
              the return of amounts contributed to the ESPP. Borrower further
              acknowledges that Lender is entitled to the return of amounts
              loaned to Borrower upon termination of participation in the plan
              or termination of employee, even if Borrower fails to provide the
              required notice.
            </li>
            <li className="font-12 font-weight-medium pt-3">
              This Agreement will terminate upon irrevocable payment in full of
              the Proceeds to Lender in cash.
            </li>
          </ol>
          <div className="font-12 text-center fw-bold pt-3 ps-4">
            ARTICLE VI EVENTS OF DEFAULT; ACCELERATION
          </div>
          <ol type="1">
            <li className="font-12 font-weight-medium pt-3">
              <b>Events of Default.</b>Each of the following shall constitute an
              “Event of Default”:
              <ol type="a">
                <li className="font-12 font-weight-medium pt-3">
                  Any representation or warranty of the Borrower in this
                  Agreement proves to have been false in any material respect
                  upon the date when made;
                </li>
                <li className="font-12 font-weight-medium pt-3">
                  Any material breach of any of Employee’s covenants contained
                  in this Agreement or any other agreement with Lender; or
                </li>
                <li className="font-12 font-weight-medium pt-3">
                  The Borrower dies or becomes incapacitated.
                </li>
              </ol>
            </li>
            <li className="font-12 font-weight-medium pt-3">
              <b>Remedies Upon Default. </b> Immediately upon the occurrence of
              any Event of Default and during the continuance thereof, the
              Lender may declare the Loan in default and Borrower must terminate
              his or her participation in the ESPP in accordance with Article V
              of this Agreement. Upon such termination, Lender shall be entitled
              to receive from Employer amounts that Lender has paid under the
              Loan in accordance with the terms of the agreement between
              Employer and Lender for the return of amounts contributed to the
              ESPP. In its sole discretion, Lender may determine not to
              terminate that Loan, but the amount of the Proceeds shall be
              adjusted to compensate Lender for any increased costs arising out
              the Event of default.
            </li>
          </ol>

          <div className="font-12 text-center fw-bold pt-3 ps-4">
            ARTICLE VII LIABILITY
          </div>
          <ol type="1">
            <li className="font-12 font-weight-medium pt-3">
              <b>General.</b>Lender shall not have any liability with respect
              to, and Borrower hereby waives, releases, and agrees not to sue
              Lender upon, any claim for any special, indirect, incidental, or
              consequential damages suffered or incurred by Borrower in
              connection with, arising out of, or in any way related to, this
              Agreement. Borrower hereby waives, releases, and agrees not to sue
              Lender for punitive damages in respect of any claim in connection
              with, arising out of, or in any way related to, this Agreement.
            </li>
            <li className="font-12 font-weight-medium pt-3">
              <b> Lender Not Fiduciary; Third Parties. </b> The relationship
              between Borrower and Lender is solely that of debtor and creditor,
              and Lender has no fiduciary or other special relationship with
              Borrower, and no term or condition of this Agreement shall be
              construed so as to deem the relationship between Borrower and
              Lender to be other than that of debtor and creditor. Lender shall
              not be held liable for any acts or omissions of any third party.
            </li>
            <li className="font-12 font-weight-medium pt-3">
              <b> Force Majeure.</b> In no event shall Lender be held liable for
              damages or any loss of any kind caused, directly or indirectly, by
              government restrictions, exchange or market rulings, suspension of
              trading, market movements, bank moratoriums, war (whether declared
              or undeclared), terrorist acts, insurrection, riots, fires,
              flooding, strikes, failure of utility services, accidents, adverse
              weather or other events of nature, including but not limited to
              earthquakes, hurricanes and tornadoes, or other events or
              conditions beyond Lender’s control or reasonable anticipation.
            </li>
          </ol>

          <div className="font-12 text-center fw-bold pt-3 ps-4">
            ARTICLE VIII MISCELLANEOUS
          </div>
          <ol type="1">
            <li className="font-12 font-weight-medium pt-3">
              <b>Third-Party Beneficiaries. </b>Nothing in this Agreement shall
              create, or be deemed to create, any third-party beneficiary rights
              in any person that is not a party to this agreement.
            </li>
            <li className="font-12 font-weight-medium pt-3">
              <b> Notice.</b> Unless otherwise expressly provided herein, all
              notices and other communications provided for hereunder shall be
              in writing (including by facsimile transmission) and mailed, faxed
              or delivered, to the address, facsimile number or, electronic mail
              address specified for notices below the signatures hereon or to
              such other address as shall be designated by such party in a
              notice to the other parties. All such other notices and other
              communications shall be deemed to have been given or made upon the
              earliest to occur of (A) actual receipt by the intended recipient
              or (B)(i) if delivered by hand or courier, when signed for by the
              designated recipient; (ii) if delivered by mail, five (5) business
              days after deposit in the mail, postage prepaid; (iii) if
              delivered by facsimile, when sent and receipt has been confirmed
              by telephone; and (iv) if delivered by electronic mail, when
              delivered.
            </li>
            <li className="font-12 font-weight-medium pt-3">
              <b> Confidentiality.</b> Except as required by Applicable Law,
              both parties agree to treat information provided in connection
              with this Agreement as confidential.
            </li>
            <li className="font-12 font-weight-medium pt-3">
              <b> Assignment; Transfers.</b> Lender may at any time assign or
              transfer (through participations or otherwise) all or part of its
              rights and/or obligations under this Agreement without Borrower’s
              consent; provided that, in any transfer through participation the
              Borrower shall continue to deal solely and directly with Lender in
              connection with this Agreement. Any reference in this Agreement to
              Lender shall also include its successors, permitted transferees or
              assigns. Borrower may not assign his or her rights or any interest
              under this Agreement without the prior written consent of Lender.
              Any attempted assignment by the Borrower in violation of this
              Agreement shall be null, void and without effect. Lender may
              disclose to its affiliates, agents and/or vendors or, subject to
              prior notice to the Borrower, any other potential assignee,
              transferee, or service provider such information about the
              Borrower and this Agreement as is reasonably necessary and
              commercially reasonable.
            </li>
            <li className="font-12 font-weight-medium pt-3">
              <b> Amendment.</b> Borrower agrees that Lender may modify the
              terms of this Agreement from time to time upon prior written
              notice when necessary or appropriate to comply with all Applicable
              Law. In any other circumstance, any amendment shall be by mutual
              written consent.
            </li>
            <li className="font-12 font-weight-medium pt-3">
              <b> Entire Agreement. </b> This Agreement and the Exhibits annexed
              hereto, which Exhibits are incorporated herein by reference and
              made a part hereof, constitute the entire Agreement between the
              parties and supersede all understandings, agreements (oral and
              written), or representations with respect to the subject matter
              hereof. This Agreement may only be amended, revised or modified
              with our written consent. Each party acknowledges that no
              representation, inducement or condition not set forth herein has
              been made or relied upon by either party.
            </li>
            <li className="font-12 font-weight-medium pt-3">
              <b> Waiver. </b> No failure by Lender to exercise any right,
              power, or privilege that it may have under this Agreement shall
              operate as a waiver thereof. Further, no waiver of any deviation
              from, or breach of, this Agreement by Borrower shall be deemed to
              be a waiver of any subsequent deviation or breach.
            </li>
            <li className="font-12 font-weight-medium pt-3">
              <b> Severability. </b> If any provision of this Agreement is
              deemed to be invalid or unenforceable or is prohibited by the laws
              of the state or jurisdiction where it is to be performed, this
              Agreement shall be considered divisible as to such provision and
              such provision shall be inoperative in such state or jurisdiction.
              The remaining provisions of this Agreement shall be valid and
              binding and of full force and effect as though such provision was
              not included. If, however, a provision of this Agreement is deemed
              to be invalid or unenforceable with respect to Lender’s ability to
              receive the Proceeds, then this Agreement shall terminate and
              Borrower must terminate his or her participation in the ESPP. Upon
              such termination, Lender shall be entitled to receive from
              Employer amounts that Lender has paid under the Loan in accordance
              with the terms of the agreement between Employer and Lender for
              the return of amounts contributed to the ESPP.
            </li>
            <li className="font-12 font-weight-medium pt-3">
              <b> Governing Law, Venue, and Jurisdiction. </b> To the extent not
              inconsistent with applicable federal law, this Agreement and any
              dispute, disagreement, or issue of construction or interpretation
              arising hereunder whether relating to its execution, its validity,
              the obligations provided herein or performance shall be governed
              or interpreted according to the laws of the State of UTAH without
              regard to choice of law considerations. Any action, suit or
              proceeding arising out of, under or in connection with this
              Agreement seeking an injunction or not otherwise required to be
              submitted to arbitration pursuant to this Agreement shall be
              brought and determined in the appropriate federal or state court
              in the State of UTAH and in no other forum. The parties hereby
              irrevocably and unconditionally submit to the personal
              jurisdiction of such courts and agree to take any and all action
              necessary to submit to the jurisdiction of such courts in any such
              suit, action or proceeding arising out of or relating to this
              Agreement.
            </li>
            <li className="font-12 font-weight-medium pt-3">
              <b> Counterparts. </b> This Agreement may be executed in one or
              more counterparts, each of which shall be deemed an original but
              all of which together shall constitute one and the same
              instrument. The execution of this Agreement may be by actual or
              facsimile signature.
            </li>
          </ol>
          <br></br>
        </div>

        <hr className="greyHr" />
        <div className="px-4">
          <Formik
            validationSchema={schema}
            onSubmit={handleSubmit}
            initialValues={initialValues}
          >
            {({ isValid, isSubmitting, values }) => (
              <Form>
                <div className="d-flex my-4">
                  <FormikCheckbox
                    className="me-2"
                    controlId="acceptTerms"
                    name="acceptTerms"
                    onChange={() => {
                      setCheckboxClicked(true);
                    }}
                  />
                  <span className="form-label fw-bold">
                    Agree to the Loan Terms and Conditions
                  </span>
                </div>
                {checkboxClicked && !lendingScrolled && (
                  <FormText className="text-danger">
                    Please read the loan agreement to the end.
                  </FormText>
                )}
                <LoaderButton
                  block
                  type="submit"
                  variant="primary"
                  size="lg"
                  text="Next"
                  className="my-2"
                  isLoading={isSubmitting}
                  disabled={
                    !isValid ||
                    isSubmitting ||
                    !values.acceptTerms ||
                    !lendingScrolled
                  }
                >
                  Next
                </LoaderButton>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </>
  );
};

export default LoadAgreement;
